import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { VmetaProject } from "common";
import {
  useCreateVmetadataTracker,
  useVmetaProjects,
} from "@via/pages/projects/projects.api.tsx";
import { SampleTrackerViewType } from "@via/pages/sample-trackers/sample-trackers-filter";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT: never[] = [];

function getProjectOwnerName(project: VmetaProject): string {
  const owner = project.owner as unknown;
  if (!owner || typeof owner !== "object") return "";
  if (!("email" in owner)) return "";
  return typeof owner.email === "string" ? owner.email : "";
}

interface UseVmetaProjectManagementOptions {
  userId: string | undefined;
  onProjectCreated?: (project: SelectOption) => void;
  viewType?: SampleTrackerViewType;
  enabled?: boolean;
  /**
   * If provided, the hook will fetch and auto-select this project id.
   * This is used when a flow is already "scoped" to a specific canvas/tracker.
   */
  initialProjectId?: string;
  /**
   * Controls whether we pass viewType/userId to the list query.
   * Some screens intentionally list all trackers; they can disable this.
   */
  enableViewTypeFilter?: boolean;
  /**
   * Project name to auto-select/create when `initialProjectId` is not provided.
   */
  defaultProjectName?: string;
  /**
   * When true and initialProjectId is provided, resolve whether that tracker
   * belongs to My/Shared and only preselect it on the matching tab.
   */
  resolveInitialProjectOwnership?: boolean;
}

interface UseVmetaProjectManagementReturn {
  vmetaProjectOptions: SelectOption[];
  selectedVmetaProject: SelectOption | null;
  setSelectedVmetaProject: (project: SelectOption | null) => void;
  isLoading: boolean;
  search: string;
  setSearch: (value: string) => void;
  fetchMoreOptions: () => void;
  createProject: (name: string) => Promise<void>;
  defaultProject: VmetaProject | null;
  resolvedInitialTrackerViewType?: SampleTrackerViewType;
}

/**
 * Custom hook to manage vmeta project selection, search, and creation.
 * Consolidates vmeta project-related state and logic (pagination + default selection).
 */
export function useVmetaProjectManagement({
  userId,
  onProjectCreated,
  viewType = SampleTrackerViewType.My,
  enabled = true,
  initialProjectId,
  enableViewTypeFilter = true,
  defaultProjectName = "default",
  resolveInitialProjectOwnership = false,
}: UseVmetaProjectManagementOptions): UseVmetaProjectManagementReturn {
  const [selectedVmetaProject, setSelectedVmetaProject] =
    useState<SelectOption | null>(null);
  const [search, setSearchState] = useState("");
  const [page, setPage] = useState(0);
  const [cumulativeVmetaProjects, setCumulativeVmetaProjects] = useState<
    VmetaProject[]
  >([]);
  const [missingVmetaProject, setMissingVmetaProject] = useState<
    VmetaProject[]
  >([]);

  const isMyView = viewType === SampleTrackerViewType.My;

  const shouldApplyViewTypeFilter =
    enableViewTypeFilter && typeof userId === "string" && userId.length > 0;

  const shouldResolveInitialProjectOwnership =
    !!initialProjectId &&
    !!userId &&
    enabled &&
    enableViewTypeFilter &&
    resolveInitialProjectOwnership;

  const {
    data: initialProjectInMyData,
    isFetched: isInitialProjectInMyFetched,
  } = useVmetaProjects(
    0,
    1,
    DEFAULT_SORT,
    initialProjectId ? [{ id: "_id", value: initialProjectId }] : [],
    shouldResolveInitialProjectOwnership,
    SampleTrackerViewType.My,
    userId
  );

  const isInitialProjectOwnershipResolved = useMemo(() => {
    if (!shouldResolveInitialProjectOwnership) return false;
    return isInitialProjectInMyFetched;
  }, [shouldResolveInitialProjectOwnership, isInitialProjectInMyFetched]);

  const isInitialProjectMy = useMemo(() => {
    if (
      !shouldResolveInitialProjectOwnership ||
      !isInitialProjectOwnershipResolved
    )
      return false;
    return (initialProjectInMyData?.data?.length ?? 0) > 0;
  }, [
    shouldResolveInitialProjectOwnership,
    isInitialProjectOwnershipResolved,
    initialProjectInMyData?.data,
  ]);

  const resolvedInitialTrackerViewType = useMemo(() => {
    if (!initialProjectId) return undefined;
    if (!resolveInitialProjectOwnership) return undefined;
    if (!isInitialProjectOwnershipResolved) return undefined;
    return isInitialProjectMy
      ? SampleTrackerViewType.My
      : SampleTrackerViewType.Shared;
  }, [
    initialProjectId,
    resolveInitialProjectOwnership,
    isInitialProjectOwnershipResolved,
    isInitialProjectMy,
  ]);

  const effectiveInitialProjectId = useMemo(() => {
    if (!initialProjectId) return undefined;
    if (!resolveInitialProjectOwnership) return initialProjectId;
    if (!isInitialProjectOwnershipResolved) return undefined;
    if (isInitialProjectMy && viewType === SampleTrackerViewType.My)
      return initialProjectId;
    if (!isInitialProjectMy && viewType === SampleTrackerViewType.Shared)
      return initialProjectId;
    return undefined;
  }, [
    initialProjectId,
    resolveInitialProjectOwnership,
    isInitialProjectOwnershipResolved,
    isInitialProjectMy,
    viewType,
  ]);

  const { data: vmetaProjects, isPending: isVmetaProjectsLoading } =
    useVmetaProjects(
      page,
      DEFAULT_PAGE_SIZE,
      DEFAULT_SORT,
      [
        {
          id: "name",
          value: search,
        },
      ],
      enabled,
      shouldApplyViewTypeFilter ? viewType : undefined,
      shouldApplyViewTypeFilter ? userId : undefined
    );

  const missingQueryFilter = useMemo(() => {
    if (effectiveInitialProjectId) {
      return [{ id: "_id", value: effectiveInitialProjectId }];
    }
    return [
      { id: "name", value: defaultProjectName },
      { id: "owner._id", value: userId },
    ];
  }, [effectiveInitialProjectId, defaultProjectName, userId]);

  const isMissingQueryEnabled = useMemo(() => {
    if (!enabled) return false;
    if (resolveInitialProjectOwnership && initialProjectId) {
      if (!isInitialProjectOwnershipResolved) return false;
      return !!effectiveInitialProjectId;
    }
    if (effectiveInitialProjectId) return true;
    return isMyView && !!userId;
  }, [
    enabled,
    resolveInitialProjectOwnership,
    initialProjectId,
    isInitialProjectOwnershipResolved,
    effectiveInitialProjectId,
    isMyView,
    userId,
  ]);

  const {
    data: vmetaMissingProject,
    isLoading: isVmetaMissingProjectsLoading,
    isSuccess: isVmetaMissingProjectSuccess,
  } = useVmetaProjects(
    0,
    20,
    DEFAULT_SORT,
    missingQueryFilter,
    isMissingQueryEnabled
  );

  const { mutateAsync: createVmetadataTracker } = useCreateVmetadataTracker();

  // Debounce search to avoid excessive API calls
  const debouncedSetSearch = useDebounceCallback((value: string) => {
    if (value === search) return;
    setPage(0);
    setSearchState(value);
  }, 300);

  const createProject = useCallback(
    async (name: string) => {
      if (!name || name.trim() === "") return;

      try {
        const trimmedLabel = name.trim();
        const body = {
          name: trimmedLabel,
          label: trimmedLabel,
        };

        const res = await createVmetadataTracker(body);
        if (res?.status === "success") {
          const newOption = {
            label: res.data.label,
            value: res.data._id,
          };
          setSelectedVmetaProject(newOption);
          onProjectCreated?.(newOption);
        }
      } catch (error) {
        console.error("Failed to create vmeta project:", error);
        throw error;
      }
    },
    [createVmetadataTracker, onProjectCreated]
  );

  // Reset state when switching between view types (only when filtering is enabled)
  useEffect(() => {
    if (!enableViewTypeFilter) return;
    setSelectedVmetaProject(null);
    setSearchState("");
    setPage(0);
    setCumulativeVmetaProjects([]);
    setMissingVmetaProject([]);
  }, [viewType, enableViewTypeFilter]);

  // Accumulate projects from paginated results
  useEffect(() => {
    if (!enabled) return;
    if (!vmetaProjects?.data) return;

    const projects = vmetaProjects.data;

    // Replace list on first page (including view switches) to avoid stale
    // options when React Query serves cached references.
    if (page === 0) {
      setCumulativeVmetaProjects(projects);
      return;
    }

    setCumulativeVmetaProjects(prev => {
      const existingIds = new Set(prev.map(p => p._id));
      const newProjects = projects.filter(
        project => !existingIds.has(project._id)
      );
      return [...prev, ...newProjects];
    });
  }, [enabled, vmetaProjects?.data, page, viewType]);

  // Handle default/initial project initialization
  useEffect(() => {
    if (!isMissingQueryEnabled) return;
    if (!isVmetaMissingProjectSuccess || !vmetaMissingProject?.data) return;

    const list: VmetaProject[] = vmetaMissingProject.data;
    setMissingVmetaProject(list);

    if (effectiveInitialProjectId) {
      const initial = list.find(
        project => project._id === effectiveInitialProjectId
      );
      if (initial) {
        setSelectedVmetaProject({
          label: initial.label,
          value: initial._id,
        });
      }
      return;
    }

    if (list.length > 0) {
      const defaultProject = list.find(
        project => project.name === defaultProjectName
      );
      if (defaultProject) {
        setSelectedVmetaProject({
          label: defaultProject.label,
          value: defaultProject._id,
        });
      } else {
        createProject(defaultProjectName);
      }
    } else {
      createProject(defaultProjectName);
    }
  }, [
    isMissingQueryEnabled,
    viewType,
    isVmetaMissingProjectSuccess,
    vmetaMissingProject?.data,
    effectiveInitialProjectId,
    defaultProjectName,
    createProject,
  ]);

  const vmetaProjectOptions = useMemo(() => {
    const map = new Map<string, VmetaProject>();

    cumulativeVmetaProjects.forEach(project => map.set(project._id, project));
    if (isMissingQueryEnabled) {
      missingVmetaProject.forEach(project => map.set(project._id, project));
    }

    return Array.from(map.values()).map(project => ({
      label: project.label,
      value: project._id,
      metadata: {
        owner: getProjectOwnerName(project),
      },
    }));
  }, [cumulativeVmetaProjects, missingVmetaProject, isMissingQueryEnabled]);

  const fetchMoreOptions = useCallback(() => {
    setPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage =
        Math.ceil((vmetaProjects?.results ?? 0) / DEFAULT_PAGE_SIZE) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [vmetaProjects?.results]);

  const defaultProject = useMemo(() => {
    if (!isMissingQueryEnabled) return null;

    return (
      missingVmetaProject.find(p => p.name === defaultProjectName) ||
      cumulativeVmetaProjects.find(p => p.name === defaultProjectName) ||
      null
    );
  }, [
    missingVmetaProject,
    cumulativeVmetaProjects,
    defaultProjectName,
    isMissingQueryEnabled,
  ]);

  return {
    vmetaProjectOptions,
    selectedVmetaProject,
    setSelectedVmetaProject,
    isLoading: isVmetaProjectsLoading || isVmetaMissingProjectsLoading,
    search,
    setSearch: debouncedSetSearch,
    fetchMoreOptions,
    createProject,
    defaultProject,
    resolvedInitialTrackerViewType,
  };
}
